import {
  BatchedMesh,
  Matrix3,
  Matrix4,
  Object3D,
  Ray,
  Raycaster,
  Vector2,
  Vector3,
  type Intersection,
  type Mesh,
  type PerspectiveCamera,
} from "three";
import type { ThreeEngine } from "@runtime/engine/engineService";
import { Emitter } from "@core/events/emitter";
import { installBoundsTreeRaycast } from "./boundsTreeRaycast";
import { MeshBoundsTreeBuilder } from "./meshBoundsTreeBuilder";

export interface PickResult {
  readonly node: Object3D;
  readonly distance: number;
  readonly worldPoint: { x: number; y: number; z: number };
  /**
   * World-space surface normal at the hit, derived from the intersected
   * face and the object's normal matrix. `null` when the hit carries no
   * face data (points / lines). Backs surface-aligned placement.
   */
  readonly worldNormal: { x: number; y: number; z: number } | null;
  readonly snapshotId: string | null;
}

export interface SnapshotNodeMetadata {
  readonly localPositionToSnapshotScale: number;
  readonly usesUnityEulerRotation: boolean;
  /**
   * Set when an animation clip writes this node's transform: the entity's
   * root snapshot, which transform handles move instead.
   */
  readonly animationOwnerSnapshotId?: string;
}

export interface SnapshotVisualHost {
  notifySnapshotTransformChanged(snapshotId: string): void;
  resolveSnapshotOutlineObjects(snapshotId: string): ReadonlyArray<Object3D>;
  setActiveOutlineSnapshotIds(snapshotIds: ReadonlySet<string>): void;
}

export type PickPredicate = (node: Object3D) => boolean;

interface BatchedPickMetadata {
  readonly sourceNode: Object3D;
  readonly snapshotId: string;
}

interface ResolvedIntersection {
  readonly hit: Intersection;
  readonly node: Object3D;
  readonly snapshotId: string | null;
}

export class PickingService {
  readonly onSnapshotVisualsChanged = new Emitter<void>();

  private m_engine: ThreeEngine | null = null;
  private m_camera: PerspectiveCamera | null = null;
  private readonly m_raycaster = new Raycaster();
  private readonly m_pointer = new Vector2();
  private readonly m_normalMatrix = new Matrix3();
  private readonly m_instanceWorldMatrix = new Matrix4();
  private m_snapshotIds = new WeakMap<Object3D, string>();
  private m_snapshotNodes = new Map<string, Object3D>();
  private m_snapshotNodeMetadata = new Map<string, SnapshotNodeMetadata>();
  private m_batchedInstances = new WeakMap<Object3D, Map<number, BatchedPickMetadata>>();
  private m_unpickable = new WeakSet<Object3D>();
  private m_snapshotVisualHost: SnapshotVisualHost | null = null;
  private m_mergedAncestors: ReadonlyMap<string, string> = new Map();
  private readonly m_boundsTrees = new MeshBoundsTreeBuilder();

  constructor() {
    // Every pick raycasts the whole scene, so the index that makes a dense
    // mesh cheap to raycast belongs to the picker. A mesh without one is
    // raycast by three's own code, unchanged (`boundsTreeRaycast.ts`).
    installBoundsTreeRaycast();
  }

  bindApp(engine: ThreeEngine): void {
    this.m_engine = engine;
  }

  bindCamera(camera: PerspectiveCamera): void {
    this.m_camera = camera;
  }

  tagSnapshot(
    node: Object3D,
    snapshotId: string,
    metadata: SnapshotNodeMetadata = {
      localPositionToSnapshotScale: 1,
      usesUnityEulerRotation: false,
    },
  ): void {
    this.m_snapshotIds.set(node, snapshotId);
    this.m_snapshotNodes.set(snapshotId, node);
    this.m_snapshotNodeMetadata.set(snapshotId, metadata);
  }

  tagBatchedInstance(
    batch: Object3D,
    instanceId: number,
    sourceNode: Object3D,
    snapshotId: string,
  ): void {
    let instances = this.m_batchedInstances.get(batch);
    if (!instances) {
      instances = new Map();
      this.m_batchedInstances.set(batch, instances);
    }
    instances.set(instanceId, { sourceNode, snapshotId });
  }

  /**
   * Parts folded into a merged node are not independently pickable: a hit
   * anywhere in the subtree resolves to the merged node. Unity gets this from
   * `MergeModel.GetRuntimeObjectInParent`, which walks up to the nearest
   * surviving `CampfireExposeToEditor` — and merging destroyed the ones on the
   * descendants. Applying it here rather than at each call site keeps hover,
   * click, context menu, grab, measure, annotate and the XR ray on one rule,
   * exactly as Unity's raycast helpers do.
   *
   * Keyed descendant id → merged node id, per
   * `buildSceneMergedAncestorIndex`. The renderer republishes this whenever it
   * retags, since both are functions of the same snapshot trees.
   */
  setMergedAncestors(mergedAncestors: ReadonlyMap<string, string>): void {
    this.m_mergedAncestors = mergedAncestors;
  }

  attachSnapshotVisualHost(host: SnapshotVisualHost | null): void {
    this.m_snapshotVisualHost = host;
  }

  notifySnapshotTransformChanged(snapshotId: string): void {
    this.m_snapshotVisualHost?.notifySnapshotTransformChanged(snapshotId);
  }

  resolveSnapshotOutlineObjects(snapshotId: string): ReadonlyArray<Object3D> {
    return this.m_snapshotVisualHost?.resolveSnapshotOutlineObjects(snapshotId)
      ?? (this.resolveSnapshotNode(snapshotId) ? [this.resolveSnapshotNode(snapshotId)!] : []);
  }

  setActiveOutlineSnapshotIds(snapshotIds: ReadonlySet<string>): void {
    this.m_snapshotVisualHost?.setActiveOutlineSnapshotIds(snapshotIds);
  }

  notifySnapshotVisualsChanged(): void {
    this.onSnapshotVisualsChanged.emit();
  }

  markUnpickable(node: Object3D): void {
    this.m_unpickable.add(node);
  }

  /**
   * Builds a spatial index for the mesh geometries under `root`, off the main
   * thread, so picks against them stop testing every triangle. Call it once a
   * placed model's meshes (and its batches' source meshes) are in their final
   * geometry; until a geometry's index arrives, its picks take three's own
   * raycast. See `meshBoundsTreeBuilder.ts`.
   */
  accelerate(root: Object3D): void {
    this.m_boundsTrees.enqueue(root);
  }

  /** Drops queued index builds — the models they were for are going away. */
  cancelPendingAcceleration(): void {
    this.m_boundsTrees.clear();
  }

  clearTags(): void {
    this.m_snapshotIds = new WeakMap<Object3D, string>();
    this.m_snapshotNodes.clear();
    this.m_snapshotNodeMetadata.clear();
    this.m_batchedInstances = new WeakMap<Object3D, Map<number, BatchedPickMetadata>>();
    this.m_mergedAncestors = new Map();
  }

  async pickFromScreen(
    x: number,
    y: number,
    predicate?: PickPredicate,
  ): Promise<PickResult | null> {
    return this.pickFromScreenSync(x, y, predicate);
  }

  pickSnapshotFromScreen(
    x: number,
    y: number,
    predicate?: PickPredicate,
  ): string | null {
    return this.pickFromScreenSync(x, y, predicate)?.snapshotId ?? null;
  }

  /**
   * Synchronous full-result pick. `pickFromScreen` is `async` only so callers
   * can be moved off the main thread later; a per-frame drag loop (the measure
   * ruler) can't await, and awaiting would let a stale result land after the
   * pointer has moved on.
   */
  pickFromScreenNow(x: number, y: number, predicate?: PickPredicate): PickResult | null {
    return this.pickFromScreenSync(x, y, predicate);
  }

  dispose(): void {
    this.m_camera = null;
    this.m_engine = null;
    this.m_boundsTrees.dispose();
    this.clearTags();
    this.m_unpickable = new WeakSet<Object3D>();
    this.m_snapshotVisualHost = null;
    this.onSnapshotVisualsChanged.clear();
  }

  resolveSnapshotId(node: Object3D): string | null {
    let cur: Object3D | null = node;
    while (cur) {
      const snapshotId = this.m_snapshotIds.get(cur);
      if (snapshotId) return this.toAddressableSnapshotId(snapshotId);
      cur = cur.parent;
    }
    return null;
  }

  /** The merged node standing in for `snapshotId`, or `snapshotId` itself. */
  private toAddressableSnapshotId(snapshotId: string): string {
    return this.m_mergedAncestors.get(snapshotId) ?? snapshotId;
  }

  resolveSnapshotNode(snapshotId: string): Object3D | null {
    return this.m_snapshotNodes.get(snapshotId) ?? null;
  }

  resolveSnapshotNodeMetadata(snapshotId: string): SnapshotNodeMetadata | null {
    return this.m_snapshotNodeMetadata.get(snapshotId) ?? null;
  }

  /**
   * The snapshot a transform edit of `snapshotId` should land on: the
   * entity's root when a clip drives this part's transform, else itself.
   */
  resolveTransformTargetSnapshotId(snapshotId: string): string {
    return this.m_snapshotNodeMetadata.get(snapshotId)?.animationOwnerSnapshotId ?? snapshotId;
  }

  screenRay(x: number, y: number): Ray | null {
    if (!this.setRayFromScreen(x, y)) return null;
    return this.m_raycaster.ray.clone();
  }

  /**
   * Ray-native pick (WebXR hand / gaze rays). Same walk + filters as the
   * screen-space path — only the ray source differs, so XR input shares the
   * exact pick semantics (visibility, unpickable overlays, snapshot tags)
   * with the desktop pointer.
   */
  pickFromRay(ray: Ray, predicate?: PickPredicate): PickResult | null {
    const engine = this.m_engine;
    if (!engine) return null;
    this.m_raycaster.ray.copy(ray);
    // Sprite raycasting needs a camera; keep whatever the last bind set.
    if (this.m_camera) this.m_raycaster.camera = this.m_camera;
    const selection = this.m_raycaster.intersectObjects(engine.root.children, true);
    const resolved = this.firstNode(selection, predicate);
    if (!resolved) return null;

    return {
      node: resolved.node,
      distance: resolved.hit.distance,
      worldPoint: toPlainVec3(resolved.hit.point),
      worldNormal: this.worldNormalOf(resolved.hit),
      snapshotId: resolved.snapshotId,
    };
  }

  private pickFromScreenSync(
    x: number,
    y: number,
    predicate?: PickPredicate,
  ): PickResult | null {
    const engine = this.m_engine;
    if (!engine || !this.setRayFromScreen(x, y)) return null;

    const selection = this.m_raycaster.intersectObjects(engine.root.children, true);
    const resolved = this.firstNode(selection, predicate);
    if (!resolved) return null;

    return {
      node: resolved.node,
      distance: resolved.hit.distance,
      worldPoint: toPlainVec3(resolved.hit.point),
      worldNormal: this.worldNormalOf(resolved.hit),
      snapshotId: resolved.snapshotId,
    };
  }

  /**
   * Transform the hit's object-space face normal into world space via the
   * object's normal matrix. Returns `null` when the intersection has no
   * face (e.g. a Points / Line hit).
   */
  private worldNormalOf(hit: Intersection): { x: number; y: number; z: number } | null {
    const localNormal = hit.face?.normal;
    if (!localNormal) return null;
    const object = hit.object as Mesh;
    object.updateWorldMatrix(true, false);
    const batchId = hit.batchId;
    if (object instanceof BatchedMesh && batchId !== undefined) {
      object.getMatrixAt(batchId, this.m_instanceWorldMatrix);
      this.m_instanceWorldMatrix.premultiply(object.matrixWorld);
      this.m_normalMatrix.getNormalMatrix(this.m_instanceWorldMatrix);
    } else {
      this.m_normalMatrix.getNormalMatrix(object.matrixWorld);
    }
    const world = localNormal
      .clone()
      .applyMatrix3(this.m_normalMatrix)
      .normalize();
    return { x: world.x, y: world.y, z: world.z };
  }

  private setRayFromScreen(x: number, y: number): boolean {
    const engine = this.m_engine;
    const camera = this.m_camera;
    if (!engine || !camera) return false;
    this.m_pointer.set(
      (x / Math.max(1, engine.canvas.clientWidth)) * 2 - 1,
      -(y / Math.max(1, engine.canvas.clientHeight)) * 2 + 1,
    );
    this.m_raycaster.setFromCamera(this.m_pointer, camera);
    return true;
  }

  private firstNode(
    selection: ReadonlyArray<Intersection>,
    predicate?: PickPredicate,
  ): ResolvedIntersection | null {
    for (const item of selection) {
      const batched = this.resolveBatchedPick(item);
      const node = batched?.sourceNode ?? item.object;
      if (!this.isVisible(item.object)) continue;
      if (!this.isVisible(node)) continue;
      if (this.isUnpickable(item.object)) continue;
      if (this.isUnpickable(node)) continue;
      if (predicate && !predicate(node)) continue;
      return {
        hit: item,
        node,
        // A batched instance carries its owner id directly, so it needs the
        // merged redirect applied here — `resolveSnapshotId` only covers the
        // unbatched walk.
        snapshotId: batched
          ? this.toAddressableSnapshotId(batched.snapshotId)
          : this.resolveSnapshotId(node),
      };
    }
    return null;
  }

  private resolveBatchedPick(item: Intersection): BatchedPickMetadata | null {
    if (item.batchId === undefined) return null;
    return this.m_batchedInstances.get(item.object)?.get(item.batchId) ?? null;
  }

  private isUnpickable(node: Object3D): boolean {
    let cur: Object3D | null = node;
    while (cur) {
      if (this.m_unpickable.has(cur)) return true;
      cur = cur.parent;
    }
    return false;
  }

  private isVisible(node: Object3D): boolean {
    let cur: Object3D | null = node;
    while (cur) {
      if (!cur.visible) return false;
      cur = cur.parent;
    }
    return true;
  }
}

function toPlainVec3(v: Vector3): { x: number; y: number; z: number } {
  return { x: v.x, y: v.y, z: v.z };
}

export const PICKING_SERVICE = Symbol.for("PickingService");
